import React, { useState } from "react";
import {
  Paper,
  Stack,
  Title,
  Text,
  Group,
  Button,
  Table,
  Badge,
  Anchor,
  Box,
  Center,
  CopyButton,
  List,
} from "@mantine/core";
import {
  IconCheck,
  IconCopy,
  IconQrcode,
  IconShieldHalf,
} from "@tabler/icons-react";
import { QRCodeSVG } from "qrcode.react";
import {
  AddressLookupResult,
  LookupFees,
  LookupUtxos,
  formatBtc,
  formatSats,
  formatUsd,
} from "../helpers/addressLookup";

/**
 * The v2 panels of the address lookup (zz-rizful-seo-enhance-oct12.txt, Part 3):
 * address + QR, "did my funds move?", coins (UTXOs) with what they cost to spend now,
 * and privacy notes. Everything is computed from the /api/address_lookup response.
 */

export const panelStyle = {
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
};

const tabular = { fontVariantNumeric: "tabular-nums" as const };

export const formatDate = (unix: number | null): string =>
  unix
    ? new Date(unix * 1000).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const timeAgo = (unix: number): string => {
  const seconds = Math.max(0, Date.now() / 1000 - unix);
  const units: [number, string][] = [
    [365 * 86400, "year"],
    [30 * 86400, "month"],
    [7 * 86400, "week"],
    [86400, "day"],
    [3600, "hour"],
    [60, "minute"],
  ];
  for (const [size, name] of units) {
    const n = Math.floor(seconds / size);
    if (n >= 1) return `${n} ${name}${n === 1 ? "" : "s"} ago`;
  }
  return "just now";
};

const sentIn = (tx: AddressLookupResult["txs"][number]): number =>
  tx.sent_sats ?? (tx.delta_sats < 0 ? -tx.delta_sats : 0);

/** The address itself, a copy button and a QR code on request. */
export const AddressQrRow: React.FC<{ address: string }> = ({ address }) => {
  const [showQr, setShowQr] = useState(false);
  return (
    <Paper p="md" radius="md" style={panelStyle}>
      <Stack gap="sm">
        <Text
          size="sm"
          c="white"
          style={{ fontFamily: "monospace", wordBreak: "break-all" }}
        >
          {address}
        </Text>
        <Group gap="xs">
          <CopyButton value={address}>
            {({ copied, copy }) => (
              <Button
                size="xs"
                variant={copied ? "filled" : "outline"}
                color={copied ? "teal" : "gray"}
                leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                onClick={copy}
              >
                {copied ? "Copied" : "Copy address"}
              </Button>
            )}
          </CopyButton>
          <Button
            size="xs"
            variant="outline"
            color="gray"
            leftSection={<IconQrcode size={14} />}
            onClick={() => setShowQr(!showQr)}
          >
            {showQr ? "Hide QR code" : "QR code"}
          </Button>
        </Group>
        {showQr && (
          <Center>
            <Box style={{ width: "100%", maxWidth: 220 }}>
              {/* QR codes need a light background to scan */}
              <QRCodeSVG
                value={`bitcoin:${address}`}
                title="Bitcoin address QR code"
                size={220}
                bgColor="#ffffff"
                fgColor="#000000"
                level="M"
                marginSize={4}
                style={{ width: "100%", height: "auto", borderRadius: 6 }}
              />
            </Box>
          </Center>
        )}
      </Stack>
    </Paper>
  );
};

/** "Did my funds move?": the latest transaction that spent from this address. */
export const FundsMovedPanel: React.FC<{ result: AddressLookupResult }> = ({ result }) => {
  const price = result.btc_usd_price;
  const lastOut = result.txs.find((tx) => sentIn(tx) > 0);
  const everSent =
    result.confirmed.sent_sats > 0 || result.unconfirmed.outgoing_sats > 0;

  let body: React.ReactNode;
  if (lastOut) {
    const sent = sentIn(lastOut);
    const back = lastOut.received_sats ?? 0;
    body = (
      <>
        <Text size="sm" c="dimmed">
          Yes. The latest transaction that spent from this address:
        </Text>
        <Title order={2} c="red.4" style={{ ...tabular, lineHeight: 1.2 }}>
          −{formatBtc(sent)}
        </Title>
        <Text size="sm" c="gray.4" style={tabular}>
          {formatSats(sent)}
          {formatUsd(sent, price) && ` · ${formatUsd(sent, price)} now`}
          {back > 0 &&
            ` · ${formatBtc(back)} came back to this address in the same transaction`}
        </Text>
        {lastOut.confirmed && lastOut.block_time ? (
          <Text size="sm" c="white">
            {timeAgo(lastOut.block_time)} · {formatDate(lastOut.block_time)} ·{" "}
            {lastOut.confirmations.toLocaleString("en-US")} confirmation
            {lastOut.confirmations === 1 ? "" : "s"}
          </Text>
        ) : (
          <Text size="sm" c="yellow.4">
            Pending: it's in the mempool and not confirmed yet.{" "}
            <Anchor
              href={`/unconfirmed-bitcoin-transaction?txid=${lastOut.txid}`}
              c="orange.4"
              size="sm"
            >
              Check when it confirms →
            </Anchor>
          </Text>
        )}
        <Anchor
          href={`https://mempool.space/tx/${lastOut.txid}`}
          target="_blank"
          rel="noopener nofollow"
          size="xs"
          c="gray.5"
          style={{ fontFamily: "monospace", wordBreak: "break-all" }}
        >
          {lastOut.txid}
        </Anchor>
      </>
    );
  } else if (!everSent) {
    body = (
      <>
        <Title order={3} c="green.4">
          No outgoing transactions
        </Title>
        <Text size="sm" c="gray.4">
          Nothing has ever left this address
          {result.unconfirmed.tx_count > 0 ? ", and nothing is leaving it in the mempool" : ""}.
        </Text>
      </>
    );
  } else {
    body = (
      <>
        <Title order={3} c="white">
          Not in the latest {result.txs.length} transactions
        </Title>
        <Text size="sm" c="gray.4">
          This address has sent {formatBtc(result.confirmed.sent_sats)} in total, but
          its most recent outgoing transaction is older than the ones listed here.{" "}
          <Anchor
            href={`https://mempool.space/address/${result.address}`}
            target="_blank"
            rel="noopener nofollow"
            c="orange.4"
            size="sm"
          >
            See the full history on mempool.space →
          </Anchor>
        </Text>
      </>
    );
  }

  return (
    <Paper p="lg" radius="md" style={panelStyle}>
      <Stack gap={6}>
        <Title order={5} c="white">
          Did my funds move?
        </Title>
        {body}
      </Stack>
    </Paper>
  );
};

const TYPE_NAMES: Record<string, string> = {
  p2pkh: "Legacy",
  p2sh: "P2SH",
  p2wpkh: "Native SegWit",
  p2wsh: "SegWit script (P2WSH)",
  p2tr: "Taproot",
};

const costOf = (vbytes: number, rate: number): number => Math.ceil(vbytes * rate);

const percentOf = (part: number, whole: number): string => {
  if (whole <= 0) return "";
  const pct = (part / whole) * 100;
  if (pct >= 100) return "more than its value";
  if (pct < 0.01) return "<0.01%";
  return `${pct < 1 ? pct.toFixed(2) : pct.toFixed(1)}%`;
};

const formatRate = (rate: number): string =>
  `${rate < 10 ? Number(rate.toFixed(2)) : Math.round(rate)} sat/vB`;

/** Unspent coins and what each costs to spend at today's fees. */
export const UtxoPanel: React.FC<{
  utxos: LookupUtxos;
  fees: LookupFees | null | undefined;
  price: number | null;
}> = ({ utxos, fees, price }) => {
  if (utxos.status === "ok" && utxos.count === 0) return null;

  const vb = utxos.input_vbytes;
  const typeName = TYPE_NAMES[utxos.type] || utxos.type;
  const sizeLine = (
    <Text size="xs" c="dimmed">
      Each coin is a separate input when you spend it: about {vb} vB for a {typeName}{" "}
      address{utxos.estimate ? ` (an estimate: ${utxos.estimate})` : ""}.
      {fees &&
        ` Fees now: fast ${formatRate(fees.fastest)}, economy ${formatRate(fees.economy)}.`}
    </Text>
  );

  if (utxos.status !== "ok") {
    return (
      <Paper p="lg" radius="md" style={panelStyle}>
        <Stack gap="xs">
          <Title order={5} c="white">
            Coins (UTXOs)
          </Title>
          {utxos.status === "too_many" ? (
            <Text size="sm" c="gray.4">
              This address holds {utxos.count.toLocaleString("en-US")} separate coins,
              more than the blockchain data service will list.
              {fees &&
                ` Spending all of them would take about ${Math.round(utxos.count * vb).toLocaleString("en-US")} vB of inputs: roughly ${formatSats(costOf(utxos.count * vb, fees.economy))} at the economy rate${formatUsd(costOf(utxos.count * vb, fees.economy), price) ? ` (${formatUsd(costOf(utxos.count * vb, fees.economy), price)})` : ""}.`}
            </Text>
          ) : (
            <Text size="sm" c="gray.4">
              We couldn't load this address's coin list right now. The balance above is
              still correct.
            </Text>
          )}
          {sizeLine}
        </Stack>
      </Paper>
    );
  }

  const list = utxos.list || [];
  const values = utxos.values || list.map((u) => u.value);
  // "small": spending it at the fast rate costs more than 1% of its value
  const small = fees ? values.filter((v) => costOf(vb, fees.fastest) > v / 100) : [];
  const consolidationVb = 10.5 + small.length * vb + utxos.output_vbytes;
  const showHint = fees != null && values.length >= 5 && small.length >= 3;

  return (
    <Paper p="lg" radius="md" style={panelStyle}>
      <Stack gap="sm">
        <Title order={5} c="white">
          Coins (UTXOs): what each costs to spend now
        </Title>
        {sizeLine}

        {showHint && fees && (
          <Text size="sm" c="yellow.4">
            {small.length} of these {values.length} coins are small: spending each one at
            the fast rate costs more than 1% of its value. Combining them into one coin
            while fees are low would cost about{" "}
            {formatSats(costOf(consolidationVb, fees.economy))} at the economy rate
            {formatUsd(costOf(consolidationVb, fees.economy), price)
              ? ` (${formatUsd(costOf(consolidationVb, fees.economy), price)})`
              : ""}
            . Combining coins links them together publicly, so only do it with coins you
            don't mind connecting.
          </Text>
        )}

        <Table.ScrollContainer minWidth={340}>
          <Table verticalSpacing="xs" style={{ background: "transparent", ...tabular }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Text size="xs" c="dimmed">Coin</Text>
                </Table.Th>
                <Table.Th style={{ textAlign: "right" }}>
                  <Text size="xs" c="dimmed">Cost now: fast</Text>
                </Table.Th>
                <Table.Th style={{ textAlign: "right" }}>
                  <Text size="xs" c="dimmed">Economy</Text>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {list.map((utxo) => {
                const fast = fees ? costOf(vb, fees.fastest) : null;
                const economy = fees ? costOf(vb, fees.economy) : null;
                const dust = utxo.value < utxos.dust_limit;
                const uneconomic = fast != null && fast >= utxo.value;
                return (
                  <Table.Tr key={`${utxo.txid}:${utxo.vout}`}>
                    <Table.Td>
                      <Anchor
                        href={`https://mempool.space/tx/${utxo.txid}#vout=${utxo.vout}`}
                        target="_blank"
                        rel="noopener nofollow"
                        size="sm"
                        c="white"
                        title={`${utxo.txid}:${utxo.vout}`}
                      >
                        {formatBtc(utxo.value)}
                      </Anchor>
                      <Group gap={4} mt={2}>
                        <Text size="xs" c="dimmed">
                          {formatSats(utxo.value)}
                        </Text>
                        {!utxo.confirmed && (
                          <Badge color="yellow" variant="light" size="xs">
                            Unconfirmed
                          </Badge>
                        )}
                        {dust && (
                          <Badge color="red" variant="light" size="xs">
                            Dust
                          </Badge>
                        )}
                        {!dust && uneconomic && (
                          <Badge color="red" variant="light" size="xs">
                            Costs more to spend than it's worth
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      {fast != null ? (
                        <>
                          <Text size="sm" c="gray.3">
                            {formatSats(fast)}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {[formatUsd(fast, price), percentOf(fast, utxo.value)]
                              .filter(Boolean)
                              .join(" · ")}
                          </Text>
                        </>
                      ) : (
                        <Text size="xs" c="dimmed">—</Text>
                      )}
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      <Text size="sm" c="gray.4">
                        {economy != null ? formatSats(economy) : "—"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        {values.length > list.length && (
          <Text size="xs" c="dimmed">
            Showing the {list.length} largest of {values.length.toLocaleString("en-US")}{" "}
            coins.
          </Text>
        )}
        {!fees && (
          <Text size="xs" c="dimmed">
            Current fee rates aren't available right now, so spending costs aren't shown.
          </Text>
        )}
      </Stack>
    </Paper>
  );
};

/** Address reuse and possible dust: information, not alarms. */
export const PrivacyNotes: React.FC<{ result: AddressLookupResult }> = ({ result }) => {
  const privacy = result.privacy;
  if (!privacy) return null;
  const reused = privacy.receive_count > 1;
  const dust = privacy.dust_tx_count > 0;
  if (!reused && !dust) return null;

  return (
    <Paper p="lg" radius="md" style={panelStyle}>
      <Stack gap="xs">
        <Group gap="xs">
          <IconShieldHalf size={18} color="#94a3b8" />
          <Title order={5} c="white">
            Privacy notes
          </Title>
        </Group>
        <List spacing="xs" size="sm" c="gray.4">
          {reused && (
            <List.Item>
              <Text size="sm" c="gray.4">
                <b>Address reuse:</b> this address has received{" "}
                {privacy.receive_count.toLocaleString("en-US")} payments
                {privacy.received_after_spend
                  ? ", including after it had already spent coins"
                  : ""}
                . Anyone who knows the address can see all of them and link them
                together. Most wallets give you a fresh address for every payment.
              </Text>
            </List.Item>
          )}
          {dust && (
            <List.Item>
              <Text size="sm" c="gray.4">
                <b>Possible dust:</b> {privacy.dust_tx_count} of the latest transactions
                sent this address a tiny amount (up to{" "}
                {privacy.dust_max_sats.toLocaleString("en-US")} sats) without spending
                from it. That can be an ordinary small payment, but tiny unsolicited
                amounts are sometimes sent to trace wallets when they're spent together
                with other coins. If you don't recognise one, many wallets let you
                freeze it ("do not spend").
              </Text>
            </List.Item>
          )}
        </List>
      </Stack>
    </Paper>
  );
};
